import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Coin } from '@/components/ui';
import { useWallet } from '@/lib/data/wallet';
import { useUnreadTotal } from '@/lib/data/conversations';
import { useAuth } from '@/lib/auth';

export function WalletChip() {
  const { wallet } = useWallet();
  const total = wallet?.balanceCoins ?? 0;

  return (
    <Link
      to="/wallet"
      className="surface inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-all duration-150 hover:-translate-y-px hover:border-[var(--brand)] hover:bg-[var(--brand-soft)]"
    >
      <Coin className="text-[var(--brand)]" />
      <span className="tabular-nums font-bold text-navy">{total}</span>
      <span className="hidden text-xs font-medium text-slate2 sm:inline">coins</span>
    </Link>
  );
}

export function UnreadBadge() {
  const unread = useUnreadTotal();
  if (unread === 0) return null;

  return (
    <span
      className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none text-white shadow-xs"
      style={{ background: 'var(--brandpink)' }}
    >
      {unread > 99 ? '99+' : unread}
    </span>
  );
}

export function AccountMenu() {
  const { profile, session, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const name = profile?.display_name?.trim() || 'Member';
  const email = session?.user.email ?? '';
  const initial = name.charAt(0).toUpperCase();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="brand-ring grid size-9.5 place-items-center rounded-full p-[2px] shadow-sm transition-transform duration-150 hover:scale-105"
      >
        <span className="grid size-full place-items-center rounded-full bg-white text-sm font-bold text-navy">
          {initial}
        </span>
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="surface-raised animate-rise absolute right-0 z-50 mt-2 w-64 rounded-2xl p-2 shadow-lg"
          >
            <div className="border-b border-slate-100 px-3 py-2.5">
              <p className="truncate text-sm font-bold text-navy">{name}</p>
              {email && <p className="truncate text-xs text-slate2">{email}</p>}
              {profile && profile.role !== 'consumer' && (
                <span className="mt-1.5 inline-block rounded-full bg-brand-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand">
                  {profile.role}
                </span>
              )}
            </div>

            <div className="py-1">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="flex items-center rounded-xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-mist hover:text-navy"
              >
                Home / About &amp; FAQ
              </Link>

              <Link
                to="/profile"
                onClick={() => setOpen(false)}
                className="flex items-center rounded-xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-mist hover:text-navy"
              >
                Your profile
              </Link>

              {(profile?.role === 'companion' || profile?.role === 'admin') && (
                <Link
                  to="/companion"
                  onClick={() => setOpen(false)}
                  className="flex items-center rounded-xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-mist hover:text-navy"
                >
                  Companion dashboard
                </Link>
              )}

              {(profile?.role === 'admin' || profile?.role === 'moderator') && (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="flex items-center rounded-xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-mist hover:text-navy"
                >
                  Admin dashboard
                </Link>
              )}

              {profile?.role === 'admin' && (
                <>
                  <Link
                    to="/admin/services"
                    onClick={() => setOpen(false)}
                    className="flex items-center rounded-xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-mist hover:text-navy"
                  >
                    Manage services
                  </Link>
                  <Link
                    to="/admin/settings"
                    onClick={() => setOpen(false)}
                    className="flex items-center rounded-xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-mist hover:text-navy"
                  >
                    Platform settings
                  </Link>
                </>
              )}
            </div>

            <div className="border-t border-slate-100 pt-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  void signOut().then(() => navigate('/', { replace: true }));
                }}
                className="flex w-full items-center rounded-xl px-3 py-2 text-left text-sm font-semibold text-danger transition-colors hover:bg-danger-soft"
              >
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
